"use client";

import * as React from "react";
import { cn } from "@/lib/utils"; // Ensure this utility is available
import { useIsMobile } from "@/hooks/useIsMobile"; // Your custom hook
import { ScrollArea } from "./ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";
import { Button } from "./ui/button"; // For Drawer trigger and role items
import { CaretDown, X } from "@phosphor-icons/react/dist/ssr";

const roles = [
  {
    uuid: "1",
    name: "I am a Founder/Entrepreneur",
    shortName: "Founder/Entrepreneur",
    value: "startup",
  },
  {
    uuid: "2",
    name: "I am an Institutional Investor",
    shortName: "Institutional Investor",
    value: "institutional_investor",
  },
  {
    uuid: "3",
    name: "I am an Angel Investor",
    shortName: "Angel Investor",
    value: "angel",
  },
  {
    uuid: "4",
    name: "I am a Knowledge Seeker/Data Junkie",
    shortName: "Knowledge Seeker/Data Junkie",
    value: "data_junkie",
  },
  {
    uuid: "5",
    name: "I am a Startup Ecosystem Builder",
    shortName: "Startup Ecosystem Builder",
    value: "startup_ecosystem_builder",
    description: "ie. Government Body, Incubator, Community Builder, Corporate Supporter, etc.",
  },
];

function SelectRole({
  theme,
  isModal = true,
  value,
  onChange,
  disabled = false,
}) {
  const isMobile = useIsMobile(); // Use your hook to detect mobile
  const [open, setOpen] = React.useState(false); // Manage Drawer open state

  // Find the selected role for display (use shortName for compact display)
  const selectedRoleObj = roles.find((role) => role.value === value);
  const selectedRoleShort = selectedRoleObj?.shortName || "-- Select --";

  if (!isMobile) {
    // Desktop: Render Select dropdown
    return (
      <Select disabled={disabled} value={value} onValueChange={onChange}>
        <SelectTrigger
          className={cn(
            isModal
              ? "bg-white text-background border-background/20 focus:ring-0"
              : "bg-white/5 hover:bg-white/10",
            theme // Apply theme prop if provided
          )}
        >
          <SelectValue placeholder='-- Select --'>
            {value ? selectedRoleShort : undefined}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {roles.map((role) => (
            <SelectItem 
              key={`select-role-${role.uuid}`} 
              value={role.value}
            >
              <div className="space-y-1">
                <div>{role.name}</div>
                {role.description && (
                  <div className="text-xs text-muted-foreground">
                    {role.description}
                  </div>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Mobile: Render Drawer
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant='outline'
          disabled={disabled}
          className={cn(
            isModal
              ? "bg-white text-background border-background/20 hover:bg-white"
              : "bg-white/5 hover:bg-white/10",
            "w-full justify-between text-left",
            theme // Apply theme prop if provided
          )}
        >
          {selectedRoleShort}
          <CaretDown className='-mr-2' weight='bold' />
        </Button>
      </DrawerTrigger>
      <DrawerContent
        className='px-5 pb-5 bg-white text-background rounded-t-3xl'
        aria-describedby={undefined}
        showHandle={false}
      >
        <div className='flex items-center justify-between pb-3 mt-4 text-center border-b border-black/10'>
          <div className='flex items-center justify-start w-12'></div>
          <span className='flex-1 text-lg font-semibold tracking-tight'>
            Select Role
          </span>
          <div className='flex items-center justify-end w-12'>
            <DrawerClose asChild>
              <button type='button'>
                <X weight='bold' className='size-5 opacity-40' />
              </button>
            </DrawerClose>
          </div>
        </div>
        <div className='pt-4 -my-2.5 divide-y divide-dashed divide-background/20'>
          {roles.map((role) => (
            <button
              key={`drawer-role-${role.uuid}`}
              className='justify-start w-full py-2.5 font-semibold text-left'
              onClick={() => {
                onChange?.(role.value); // Trigger onChange with selected value
                setOpen(false); // Close Drawer
              }}
            >
              <div className="flex flex-col items-start">
                <span>{role.name}</span>
                {role.description && (
                  <span className="text-xs text-background/60 mt-1 font-normal">
                    {role.description}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default SelectRole;
